import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Upload, Download, Trash2, Send, FileText, FileCode } from "lucide-react";
import { fechaHoraMX } from "@/lib/format";

export const descargarFactura = async (pedido, tipo) => {
  try {
    const res = await api.get(`/pedidos/${pedido.id}/factura/${tipo}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = pedido[`factura_${tipo}`]?.nombre || `${pedido.numero_pedido}.${tipo}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) { toast.error("No se pudo descargar la factura"); }
};

const ESTADOS_OK = ["Entregado", "Facturación", "Vencimiento", "Pago", "Pedido cerrado"];

function ArchivoFila({ pedido, tipo, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const info = pedido[`factura_${tipo}`];
  const Icon = tipo === "pdf" ? FileText : FileCode;
  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(`.${tipo}`)) { toast.error(`Selecciona un archivo .${tipo.toUpperCase()}`); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/pedidos/${pedido.id}/factura/${tipo}`, fd);
      onChange(data); toast.success(`Factura ${tipo.toUpperCase()} cargada`);
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setBusy(false); }
  };
  const quitar = async () => {
    if (!window.confirm(`¿Quitar la factura ${tipo.toUpperCase()}?`)) return;
    try { const { data } = await api.delete(`/pedidos/${pedido.id}/factura/${tipo}`); onChange(data); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2" data-testid={`factura-fila-${tipo}`}>
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="text-sm flex-1 truncate" data-testid={`factura-nombre-${tipo}`}>{info ? info.nombre : <span className="text-muted-foreground">Sin {tipo.toUpperCase()}</span>}</span>
      <input ref={ref} type="file" accept={tipo === "pdf" ? ".pdf,application/pdf" : ".xml,text/xml,application/xml"} className="hidden" onChange={subir} data-testid={`factura-input-${tipo}`} />
      {info && <Button type="button" variant="ghost" size="icon" title="Descargar" onClick={() => descargarFactura(pedido, tipo)} data-testid={`factura-descargar-${tipo}`}><Download className="h-4 w-4" /></Button>}
      {info && <Button type="button" variant="ghost" size="icon" title="Quitar" className="text-red-600" onClick={quitar} data-testid={`factura-quitar-${tipo}`}><Trash2 className="h-4 w-4" /></Button>}
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid={`factura-subir-${tipo}`}><Upload className="h-4 w-4 mr-1" />{busy ? "Subiendo..." : info ? "Reemplazar" : `Subir ${tipo.toUpperCase()}`}</Button>
    </div>
  );
}

export default function FacturaArchivos({ pedido, estatusGuardado, onChange }) {
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const bloqueada = !!pedido.incidencia && pedido.incidencia.estado !== "resuelta";
  const habilitado = !bloqueada && ESTADOS_OK.includes(estatusGuardado ?? pedido.estatus);
  const hayArchivo = pedido.factura_pdf || pedido.factura_xml;
  const enviar = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/pedidos/${pedido.id}/factura-enviar`);
      onChange(data.pedido);
      if (data.email === "enviado") toast.success(`Factura enviada a ${data.vendedor}`, { description: `Aviso en el CRM y correo a ${data.email_destino}` });
      else if (data.email === "sin_correo") toast.warning(`Aviso enviado a ${data.vendedor}`, { description: "El vendedor no tiene correo registrado; no se envió email." });
      else toast.warning(`Aviso enviado a ${data.vendedor}`, { description: "El correo no se pudo enviar en este momento." });
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSending(false); setConfirm(false); }
  };
  return (
    <div className="space-y-2 md:col-span-2 border rounded-lg p-3 bg-slate-50" data-testid="factura-archivos">
      <Label>Archivos de la factura</Label>
      {!habilitado ? (
        <p className="text-xs text-muted-foreground" data-testid="factura-no-disponible">{bloqueada ? "Facturación bloqueada: primero resuelve la incidencia de entrega." : "Podrás subir la factura cuando el pedido esté en \"Entregado\" o después (guarda primero el cambio de estatus)."}</p>
      ) : (
        <>
          <ArchivoFila pedido={pedido} tipo="pdf" onChange={onChange} />
          <ArchivoFila pedido={pedido} tipo="xml" onChange={onChange} />
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-xs text-muted-foreground" data-testid="factura-enviada-info">{pedido.factura_enviada_at ? `Enviada el ${fechaHoraMX(pedido.factura_enviada_at)}` : "Aún no se ha enviado al vendedor"}</span>
            <Button type="button" size="sm" disabled={!hayArchivo || sending} onClick={() => setConfirm(true)} data-testid="factura-enviar"><Send className="h-4 w-4 mr-1" />{sending ? "Enviando..." : "Enviar factura al vendedor"}</Button>
          </div>
        </>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="factura-enviar-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar factura{pedido.factura ? ` ${pedido.factura}` : ""} a {pedido.vendedor?.full_name || "el vendedor"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pedido.factura_enviada_at ? `Ya se envió el ${fechaHoraMX(pedido.factura_enviada_at)}; se mandará de nuevo. ` : ""}
              Le llegará un aviso en el módulo Avisos con los archivos adjuntos y un correo electrónico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="factura-enviar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); enviar(); }} disabled={sending} data-testid="factura-enviar-confirmar">{sending ? "Enviando..." : "Sí, enviar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
